// ─────────────────────────────────────────────────────────────────────────────
// Reglas de campaña de la Reportería de Brillamos (docs/contexto/REPORTERIA-REGLAS.md §8, acuerdos
// del War Room del 2026-09-21). Lógica PURA, sin React: la usan la tira de filtros (selector de
// campaña en cascada), Brillamos Juntos (bloque «Alcance de la campaña») y las tablas de campañas
// (columna Alcance), y así se prueba sin montar pantallas.
// ─────────────────────────────────────────────────────────────────────────────
import type { CampaignDto, CampaignStatus } from '@/api/recognition';
import type { OUItem } from '@/api/types';

/**
 * BJ-F4 — los ÚNICOS estados que se ofrecen en Reportería. Borrador y Pendiente de aprobación no
 * tienen datos (Rubi 35:26, Adriana 36:05). Se elige uno u otro, nunca mezclados (Laura 37:12).
 */
export type EstadoCampanaReporte = Extract<CampaignStatus, 'Activa' | 'Finalizada'>;
export const ESTADOS_CAMPANA_REPORTE: readonly EstadoCampanaReporte[] = ['Activa', 'Finalizada'];

/** Mapa hijo → padre del catálogo de OUs. */
function padresDe(ouItems: readonly OUItem[]): Map<string, string> {
  const padres = new Map<string, string>();
  for (const ou of ouItems) {
    if (ou.parentOuId) padres.set(ou.id, ou.parentOuId);
  }
  return padres;
}

/** Ids de `ouId` y de todos sus ancestros, en orden de subida. Corta ciclos por seguridad. */
function linajeDe(ouId: string, padres: ReadonlyMap<string, string>): string[] {
  const linaje: string[] = [];
  const vistos = new Set<string>();
  let actual: string | undefined = ouId;
  while (actual && !vistos.has(actual)) {
    vistos.add(actual);
    linaje.push(actual);
    actual = padres.get(actual);
  }
  return linaje;
}

/**
 * BJ-H4 — el alcance truncado al NIVEL MÁS ALTO elegido: una OU del alcance se muestra solo si
 * ninguno de sus ancestros también está en el alcance. Se eligió Europa (y el árbol metió sus hijas)
 * ⇒ «Europa»; se eligió solo Dirección Perú ⇒ «Dirección Perú» (Rubi 54:24–54:55).
 *
 * Conserva el orden del alcance original. Una OU que el catálogo no conoce se deja tal cual: no se
 * puede saber si tiene un ancestro elegido, y ocultarla escondería parte del alcance.
 */
export function alcanceNivelMasAlto(alcance: readonly string[], ouItems: readonly OUItem[]): string[] {
  if (alcance.length <= 1) return [...alcance];
  const padres = padresDe(ouItems);
  const enAlcance = new Set(alcance);
  return alcance.filter((ouId) => !linajeDe(ouId, padres).slice(1).some((ancestro) => enAlcance.has(ancestro)));
}

/**
 * BJ-F3 — cascada OU → campañas: ¿la campaña aplica a alguna de las OUs elegidas arriba?
 *
 * Aplica cuando una OU elegida está DENTRO del alcance (es una OU del alcance o descendiente de una)
 * o cuando una OU del alcance está DENTRO de la elegida (se eligió México y la campaña es de una BU
 * de México). Alcance vacío = campaña global ⇒ aplica a todas. Sin OUs elegidas ⇒ todas aplican.
 *
 * 🔴 Lee `campaign.alcance` —el campo de alcance real—, nunca el nombre de la campaña, que es texto
 * libre (Laura 24:28).
 */
export function campanaAplicaAOus(
  alcance: readonly string[],
  ousElegidas: readonly string[],
  ouItems: readonly OUItem[],
): boolean {
  if (ousElegidas.length === 0 || alcance.length === 0) return true;
  const padres = padresDe(ouItems);
  const enAlcance = new Set(alcance);
  const elegidas = new Set(ousElegidas);
  return ousElegidas.some((ou) => linajeDe(ou, padres).some((id) => enAlcance.has(id)))
    || alcance.some((ou) => linajeDe(ou, padres).some((id) => elegidas.has(id)));
}

/** Resultado de `ousFrenteAlAlcance`: qué OUs entran de verdad en el reporte y cuáles no. */
export interface OusFrenteAlAlcance {
  /** OUs que sí se cuentan: la elegida si cae dentro del alcance, o la del alcance que cae dentro de ella. */
  efectivas: string[];
  /** OUs elegidas que la campaña no cubre: no aportan nada al reporte. */
  fuera: string[];
}

/**
 * 2026-10-14 («Bugs Brillamos2.docx», BJF-1) — el reporte de Brillamos Juntos es POR CAMPAÑA y manda
 * su alcance (BJ-F2): la OU de la barra acota dentro de él y nunca lo amplía. Con una campaña de FOSE,
 * elegir «Todas las OUs» o las seis OUs da las mismas cifras que elegir FOSE, y «Mostrando» decía las
 * seis. Esto separa las OUs elegidas en las que la campaña cubre y las que no, para decirlo en pantalla.
 *
 * Sin OUs elegidas cuenta el alcance completo (truncado al nivel más alto); campaña global (alcance
 * vacío) ⇒ toda OU elegida entra.
 */
export function ousFrenteAlAlcance(
  alcance: readonly string[],
  ousElegidas: readonly string[],
  ouItems: readonly OUItem[],
): OusFrenteAlAlcance {
  if (alcance.length === 0) return { efectivas: [...ousElegidas], fuera: [] };
  const alcanceVisible = alcanceNivelMasAlto(alcance, ouItems);
  if (ousElegidas.length === 0) return { efectivas: alcanceVisible, fuera: [] };

  const padres = padresDe(ouItems);
  const enAlcance = new Set(alcance);
  const efectivas = new Set<string>();
  const fuera: string[] = [];
  for (const ou of ousElegidas) {
    if (linajeDe(ou, padres).some((id) => enAlcance.has(id))) {
      efectivas.add(ou);
      continue;
    }
    const delAlcanceDentro = alcanceVisible.filter((a) => linajeDe(a, padres).includes(ou));
    if (delAlcanceDentro.length > 0) delAlcanceDentro.forEach((a) => efectivas.add(a));
    else fuera.push(ou);
  }
  return { efectivas: [...efectivas], fuera };
}

/**
 * 2026-10-27 (reunión «reunion-27102026.docx») — OU que toma la barra al elegir una campaña. La hoja
 * «Todos los reconocimientos Recibidos» del Excel se pide por la OU de la barra, no por la campaña
 * (Error 2 del 2026-10-02): al pasar de una campaña de México a una de FOSE sin tocar la OU, el libro
 * de FOSE salía con los recibidos de México. Ahora la OU sigue a la campaña.
 *
 *   · `null` = la barra no se toca: campaña global (alcance vacío), o la selección ya cae dentro del
 *     alcance (se eligió Rumania y la campaña es de Europa: se respeta el recorte más fino).
 *   · Si no, el alcance truncado al nivel más alto. Para un administrador acotado (`permitidas`) solo
 *     las OUs de su alcance; si ninguna lo es, `null` (su OU fija ya manda).
 */
export function ousDeLaBarraParaCampana(
  alcance: readonly string[],
  ousElegidas: readonly string[],
  ouItems: readonly OUItem[],
  permitidas: ReadonlySet<string> | null,
): string[] | null {
  if (alcance.length === 0) return null;
  const padres = padresDe(ouItems);
  const enAlcance = new Set(alcance);
  const dentro = ousElegidas.length > 0
    && ousElegidas.every((ou) => linajeDe(ou, padres).some((id) => enAlcance.has(id)));
  if (dentro) return null;
  const visibles = alcanceNivelMasAlto(alcance, ouItems).filter((ou) => !permitidas || permitidas.has(ou));
  return visibles.length > 0 ? visibles : null;
}

/**
 * Lista del combo de campañas: solo del estado elegido y que apliquen a las OUs de arriba. No filtra
 * por tipo de campaña: el combo ofrecía todas antes de esta regla y la minuta no pide cambiarlo.
 */
export function campanasDelCombo(
  campanas: readonly CampaignDto[],
  estado: EstadoCampanaReporte,
  ousElegidas: readonly string[],
  ouItems: readonly OUItem[],
): CampaignDto[] {
  return campanas.filter((c) => c.estado === estado && campanaAplicaAOus(c.alcance ?? [], ousElegidas, ouItems));
}

/**
 * 2026-10-14 (BJT-1, «Bugs Brillamos2.docx») — valor del selector de campaña para «Todas las
 * campañas»: consolida las campañas Brillamos que el combo lista (estatus y OUs elegidas). Nunca es el
 * id de una campaña real.
 */
export const TODAS_LAS_CAMPANAS = '__todas__';

/** Las campañas que entran en «Todas las campañas»: las Brillamos del combo. */
export function campanasDeTodas(combo: readonly CampaignDto[]): CampaignDto[] {
  return combo.filter((c) => c.tipo === 'Brillamos');
}

/**
 * 2026-10-14 (BJT-1) — la campaña COMBINADA de «Todas las campañas», para que el reporte la trate
 * como una sola (alcance, vigencia, puntos) sin otra rama de código:
 *   · alcance = la UNIÓN de los alcances; vacío (llega a todos) si alguna es global;
 *   · vigencia = el inicio más temprano y el fin más tardío (sin límite si alguna no lo tiene);
 *   · estado = Finalizada solo si todas lo están (BJ-F5: entonces no aplican las fechas);
 *   · «Aplican puntos» si alguna los otorga.
 * Su id es {@link TODAS_LAS_CAMPANAS}: las consultas mandan la lista de ids, nunca este valor.
 */
export function campanaCombinada(campanas: readonly CampaignDto[], nombre: string): CampaignDto | null {
  if (campanas.length === 0) return null;
  const algunaGlobal = campanas.some((c) => (c.alcance ?? []).length === 0);
  const inicios = campanas.map((c) => c.fechaInicio);
  const fines = campanas.map((c) => c.fechaFin);
  const base = campanas[0];
  return {
    ...base,
    id: TODAS_LAS_CAMPANAS,
    nombre: Object.fromEntries(Object.keys(base.nombre ?? { es: '' }).map((lang) => [lang, nombre])),
    descripcion: null,
    estado: campanas.every((c) => c.estado === 'Finalizada') ? 'Finalizada' : 'Activa',
    alcance: algunaGlobal ? [] : [...new Set(campanas.flatMap((c) => c.alcance ?? []))],
    fechaInicio: inicios.includes(null) ? null : [...inicios as string[]].sort((a, b) => a.localeCompare(b))[0],
    fechaFin: fines.includes(null) ? null : [...fines as string[]].sort((a, b) => b.localeCompare(a))[0],
    visibilidad: { ...base.visibilidad, aplicanPuntos: campanas.some((c) => Boolean(c.visibilidad?.aplicanPuntos)) },
  };
}

/**
 * ¿La campaña ya CERRÓ? Es lo que decide qué campañas lista la pestaña «Históricos».
 *
 * Cerrada = estado `Finalizada` **o** fecha de fin ya vencida. Nunca un `Borrador`: un borrador no ha
 * empezado, aunque alguien le haya escrito una fecha de fin pasada.
 *
 * 🔴 Mirar solo el estado no basta. Una campaña sigue `Activa` tras su fecha de fin hasta que corre
 * `ExpireDueCampaigns`, y un comentario de SurveyService (24-sep-2026) dice que ese job no había
 * llegado a correr en Producción: con la regla «solo Finalizada», esas campañas no saldrían en ninguna
 * pestaña. Decisión del cliente (2026-09-29): cuentan las dos.
 *
 * El borde es `fin <= ahora`, el mismo que usa el backend (`AntiSpoilerPolicy.CampanaProtegida`,
 * `PostEditPolicy.CampanaTerminada`): una campaña que termina exactamente ahora ya está cerrada.
 */
export function campanaCerrada(
  campana: Pick<CampaignDto, 'estado' | 'fechaFin'>,
  ahora: Date = new Date(),
): boolean {
  if (campana.estado === 'Borrador') return false;
  if (campana.estado === 'Finalizada') return true;
  if (!campana.fechaFin) return false;
  return new Date(campana.fechaFin).getTime() <= ahora.getTime();
}

const MS_POR_DIA = 24 * 60 * 60 * 1000;

/**
 * Días que dura una campaña, de su inicio a su fin — columna «Duración de la campaña» del Excel
 * global de Históricos (2026-10-06, pedido del cliente: antes eran meses redondos y una campaña de
 * pocos días salía como «0 meses»).
 *
 * Se redondea hacia arriba porque el fin suele guardarse al cierre del día (23:59:59): del 1 al 31 de
 * octubre son 31 días, no 30. `null` si falta alguna fecha o no es válida; nunca negativo.
 */
export function diasDeCampana(inicio: string | null, fin: string | null): number | null {
  if (!inicio || !fin) return null;
  const ms = new Date(fin).getTime() - new Date(inicio).getTime();
  if (Number.isNaN(ms)) return null;
  return Math.max(Math.ceil(ms / MS_POR_DIA), 0);
}
